/* eslint-disable no-param-reassign */
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { api } from "jiffy-api";

const state = () => ({
  channelUuid: null,
  isUploading: false,
  uploadError: null,
  deliveryDate: null,
  uploadResponse: null
});

const mutations = {
  setChannelUuid(stateObj, uuid) {
    stateObj.channelUuid = uuid;
  },

  setUploadingStatus(stateObj, status) {
    stateObj.isUploading = status;
  },

  setUploadError(stateObj, error) {
    stateObj.uploadError = error;
  },

  setDeliveryDate(stateObj, date) {
    stateObj.deliveryDate = date;
  },

  setUploadResponse(stateObj, response) {
    stateObj.uploadResponse = response;
  }
};

const getters = {
  channelUuid: stateObj => stateObj.channelUuid,
  isUploading: stateObj => stateObj.isUploading,
  uploadError: stateObj => stateObj.uploadError,
  deliveryDate: stateObj => stateObj.deliveryDate,
  uploadResponse: stateObj => stateObj.uploadResponse,

  // BOGO Transfers Promotion
  // eslint-disable-next-line max-params
  blanksQuantity: (_state, _getters, _rootState, rootGetters) =>
    rootGetters.allLineItems
      .filter(({ businessLine }) => businessLine === "Blanks")
      .reduce((sum, { quantity }) => sum + quantity, 0),

  // eslint-disable-next-line max-params
  standardTransfersQuantity: (_state, _getters, _rootState, rootGetters) =>
    rootGetters.allLineItems
      .filter(({ productStyle }) => productStyle === "DTF001")
      .reduce((sum, { quantity }) => sum + quantity, 0),

  remainingFreeTransfers: (_state, localGetters) =>
    Math.max(0, localGetters.blanksQuantity - localGetters.standardTransfersQuantity),

  isEligibleForBogoPromotion: (_state, _getters, rootState) =>
    rootState.cartState.isEligibleForBogoPromotion,

  isEligibleForBogoTransfers: (_state, localGetters) => {
    const backendEligible = localGetters.isEligibleForBogoPromotion;
    const hasRemainingTransfers = localGetters.remainingFreeTransfers > 0;
    return backendEligible && hasRemainingTransfers;
  }
};

const actions = {
  async initDtfChannel({ commit }, { catalogNumber }) {
    try {
      const response = await api.dtf.getInitConfig({ catalog_number: catalogNumber });
      const { dtfChannelUuid } = objNamesToCamelCase(response);
      commit("setChannelUuid", dtfChannelUuid);
    } catch (error) {
      console.warn("Failed to initialize DTF channel", error);
      throw error;
    }
  },

  async uploadArtwork({ commit }, formData) {
    commit("setUploadingStatus", true);
    commit("setUploadError", null);
    commit("setUploadResponse", null);

    try {
      const response = await api.dtf.uploadArtwork(formData, {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true
      });
      commit("setUploadingStatus", false);
      commit("setUploadResponse", response);
    } catch (error) {
      commit("setUploadingStatus", false);
      const errorResponse = error.response?.data;
      const errorMessage = errorResponse?.error || error.message;
      const finalError = errorMessage === "Network Error" ? "Upload error" : errorMessage;
      commit("setUploadError", finalError);
      throw error;
    }
  },

  async fetchDeliveryDate({ commit }) {
    try {
      const response = await api.product.fetchBusinessDeliveryDates({
        business_line: "Transfers"
      });

      if (response.success && response.composition_ids?.length > 0) {
        const firstCompositionId = response.composition_ids[0];
        const deliveryData = response.estimates?.[firstCompositionId];
        const date = deliveryData?.date || null;
        commit("setDeliveryDate", date);
      } else {
        commit("setDeliveryDate", null);
      }
    } catch (error) {
      commit("setDeliveryDate", null);
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
